import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Stack
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import api, { inspectionCycleRulesApi } from '../../../services/api';

const EditInspectionModal = ({ open, onClose, inspection, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [cycleFrequency, setCycleFrequency] = useState(null);
  const [loadingFrequency, setLoadingFrequency] = useState(false);
  const [cycleWarning, setCycleWarning] = useState(null);

  // Form state - only editable fields
  const [formData, setFormData] = useState({
    inspection_date: new Date(),
    cycle: '',
    notes: ''
  });

  // Populate form when inspection prop changes
  useEffect(() => {
    if (!open || !inspection) return;

    setFormData({
      inspection_date: inspection.date ? new Date(inspection.date) : new Date(),
      cycle: inspection.cycle ? inspection.cycle.toString() : '',
      notes: inspection.description === 'No description provided' ? '' : (inspection.description || '')
    });
    setSuccess(false);
    setError(null);
    setCycleWarning(null);

    // Fetch cycle frequency for the inspection type
    if (inspection.type && inspection.asset_id) {
      fetchCycleFrequency(inspection.asset_id, inspection.type);
    }
  }, [open, inspection]);

  // Check for duplicate cycle when cycle or date changes
  useEffect(() => {
    if (!open || !inspection || !formData.cycle || !formData.inspection_date) {
      setCycleWarning(null);
      return;
    }

    const checkCycle = async () => {
      try {
        const year = new Date(formData.inspection_date).getFullYear();
        const response = await api.get('/inspections/check-cycle', {
          params: {
            asset_id: inspection.asset_id,
            inspection_type_id: inspection.type,
            cycle: formData.cycle,
            year,
            exclude_id: inspection.id
          }
        });
        if (response.data.exists) {
          setCycleWarning(`An inspection with this cycle already exists (ID: ${response.data.inspection_id})`);
        } else {
          setCycleWarning(null);
        }
      } catch (err) {
        console.error('Failed to check cycle:', err);
      }
    };

    checkCycle();
  }, [open, inspection, formData.cycle, formData.inspection_date]);

  const fetchCycleFrequency = async (assetId, inspectionTypeId) => {
    setLoadingFrequency(true);
    try {
      const response = await inspectionCycleRulesApi.getEffectiveCycle({
        asset_id: assetId,
        inspection_type_id: inspectionTypeId
      });
      setCycleFrequency(response.data.cycle_frequency || null);
    } catch (err) {
      console.error('Failed to fetch cycle frequency:', err);
      setCycleFrequency(null);
    } finally {
      setLoadingFrequency(false);
    }
  };

  const handleDateChange = (newDate) => {
    setFormData(prev => ({ ...prev, inspection_date: newDate }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!inspection) return;

    setLoading(true);
    setError(null);

    try {
      const updateData = {
        inspection_date: formData.inspection_date
          ? formData.inspection_date.toISOString().split('T')[0]
          : undefined,
        cycle: formData.cycle ? parseInt(formData.cycle) : undefined,
        notes: formData.notes
      };

      await api.put(`/inspections/${inspection.id}`, updateData);

      setSuccess(true);

      if (onSuccess) {
        onSuccess();
      }

      setTimeout(() => {
        onClose();
      }, 800);
    } catch (err) {
      let errorMessage = 'Failed to update inspection';
      if (err.response) {
        errorMessage = `API Error: ${err.response.status} - ${err.response.data?.error || ''}`;
      } else if (err.request) {
        errorMessage = 'No response received from server';
      } else {
        errorMessage = `Error: ${err.message}`;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Generate cycle options based on frequency
  const generateCycleOptions = (frequency) => {
    if (!frequency) return [];

    let maxCycles = 1;
    let cycleName = 'Cycle';

    switch (frequency.toLowerCase()) {
      case 'weekly':
        maxCycles = 52;
        cycleName = 'Week';
        break;
      case 'monthly':
        maxCycles = 12;
        cycleName = 'Month';
        break;
      case 'quarterly':
        maxCycles = 4;
        cycleName = 'Quarter';
        break;
      case 'biannual':
        maxCycles = 2;
        cycleName = 'Half-Year';
        break;
      case 'annual':
        maxCycles = 1;
        cycleName = 'Year';
        break;
      case 'ad-hoc':
        return [
          <MenuItem key="1" value="1">
            Ad-hoc Inspection
          </MenuItem>
        ];
      case 'bimonthly':
        maxCycles = 6;
        cycleName = 'Period';
        break;
      case 'every_4_months':
        maxCycles = 3;
        cycleName = 'Period';
        break;
      case 'every_18_months':
      case 'biennial':
        maxCycles = 1;
        cycleName = 'Cycle';
        break;
      default:
        return [
          <MenuItem key="not-applicable" value="1" disabled>
            Cycle not applicable
          </MenuItem>
        ];
    }

    const options = [];
    for (let i = 1; i <= maxCycles; i++) {
      options.push(
        <MenuItem key={i} value={i.toString()}>
          {cycleName} {i}
        </MenuItem>
      );
    }
    return options;
  };

  const isFormValid = () => {
    return formData.inspection_date && formData.cycle;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <EditIcon /> Edit Inspection #{inspection?.id}
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Inspection updated successfully!
          </Alert>
        )}

        {loadingFrequency ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Read-only info */}
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Inspection Type:</strong> {inspection?.title || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                <strong>Status:</strong> {inspection?.status || 'N/A'}
              </Typography>
            </Box>

            {/* Editable: Inspection Date */}
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Inspection Date"
                value={formData.inspection_date}
                onChange={handleDateChange}
                disabled={loading || success}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined'
                  }
                }}
              />
            </LocalizationProvider>

            {/* Editable: Cycle */}
            <FormControl fullWidth>
              <InputLabel id="edit-cycle-label">Inspection Cycle</InputLabel>
              <Select
                labelId="edit-cycle-label"
                id="cycle"
                name="cycle"
                value={formData.cycle}
                onChange={handleChange}
                label="Inspection Cycle"
                disabled={loading || success || !cycleFrequency}
              >
                {cycleFrequency ? (
                  generateCycleOptions(cycleFrequency)
                ) : (
                  <MenuItem disabled value="">
                    Loading cycle options...
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            {/* Cycle duplicate warning */}
            {cycleWarning && (
              <Alert severity="warning">
                {cycleWarning}
              </Alert>
            )}

            {/* Editable: Notes */}
            <TextField
              id="notes"
              name="notes"
              label="Notes"
              multiline
              rows={4}
              value={formData.notes}
              onChange={handleChange}
              fullWidth
              disabled={loading || success}
              variant="outlined"
            />
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
        <Button
          onClick={onClose}
          color="inherit"
          disabled={loading}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={!isFormValid() || loading || success}
          sx={{ px: 3 }}
        >
          {loading ? <CircularProgress size={24} /> : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditInspectionModal;
